import * as React from 'react';
import { useStore } from 'react-redux';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { transformModelToSnapshot } from 'src/containers/ProjectDiagram/utils/transformModelToSnapshot';
import { useGenerateModelAndMockData } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useGenerateModelAndMockData';
import { pendingAutoGenerateMockDataSelector } from 'src/selectors/pendingAutoGenerateMockData';
import { projectSelector } from 'src/selectors/project';
import { ReduxState } from 'src/store/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';

export const useAutoGenerateMockData = () => {
  const dispatch = useReduxDispatch();
  const store = useStore<ReduxState>();
  const { projectId } = useRouterParams();
  const generateModelAndMockData = useGenerateModelAndMockData();
  const { evaluateSummary } = useEvaluateSummary();

  const portalFeatures = usePortalFeatures();
  const isVoiceboxEnabled = useIsVoiceboxEnabled();
  const project = useReduxSelector(projectSelector(projectId));
  const useVoiceboxSuggestions =
    Boolean(isVoiceboxEnabled && portalFeatures.voiceboxSuggestions) &&
    !project?.disableVoiceboxSuggestions;

  const isPending = useReduxSelector(
    pendingAutoGenerateMockDataSelector(projectId)
  );

  const [isGenerating, setIsGenerating] = React.useState(false);
  const [hasTriggered, setHasTriggered] = React.useState(false);

  React.useEffect(() => {
    if (hasTriggered || isGenerating || !isPending) {
      return;
    }

    setHasTriggered(true);

    const runAutoGenerate = async () => {
      setIsGenerating(true);

      try {
        const result = await generateModelAndMockData();
        if (useVoiceboxSuggestions && result.diagram) {
          const updatedProject = projectSelector(projectId)(store.getState());
          if (updatedProject?.diagram) {
            const snapshot = { ...updatedProject.diagram };
            snapshot.model = transformModelToSnapshot(result.diagram.model);
            evaluateSummary({ ...updatedProject, diagram: snapshot });
          }
        }
      } catch (error) {
        console.error('Failed to auto-generate mock data:', error);
      } finally {
        dispatch(
          projectsActionCreators.clearedPendingAutoGenerateMockData(projectId)
        );
        setIsGenerating(false);
        dispatch(
          uiSlice.actions.toggledCreateProjectOverlay({ isOpen: false })
        );
      }
    };

    runAutoGenerate();
  }, [
    dispatch,
    evaluateSummary,
    generateModelAndMockData,
    hasTriggered,
    isGenerating,
    isPending,
    projectId,
    store,
    useVoiceboxSuggestions,
  ]);

  return { isGenerating };
};
